import { useSaveDraftContent } from './useSaveDraftContent';
import { IDraft } from '../../@types';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useDeleteDraft } from './useDeleteDraft';
import { useDialog } from '../useDialog';

interface Props {
  refetchData: () => void;
}

export const useDraftManager = ({ refetchData }: Props) => {
  const [selectedDraft, setSelectedDraft] = useState<IDraft | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const { mutate: mutateDeleteDraft, isLoading: isDeleteLoading } = useDeleteDraft();
  const { mutate: mutateSaveDraftContent, isLoading: isRenameLoading } = useSaveDraftContent();

  const {
    isDialogOpen: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog();

  const {
    isDialogOpen: isRenameDialogOpen,
    openDialog: openRenameDialog,
    closeDialog: closeRenameDialog,
  } = useDialog();

  const {
    isDialogOpen: isViewDialogOpen,
    openDialog: openViewDialog,
    closeDialog: closeViewDialog,
  } = useDialog();

  const handleOpenRenameDialog = (draft: IDraft) => {
    setSelectedDraft(draft);
    openRenameDialog();
  };

  const handleOpenDeleteDialog = (draft: IDraft) => {
    setSelectedDraft(draft);
    openDeleteDialog();
  };

  const handleOpenViewDialog = (draft: IDraft) => {
    setSelectedDraft(draft);
    openViewDialog();
  };

  const handleConfirmDelete = () => {
    if (selectedDraft) {
      mutateDeleteDraft(
        { id: selectedDraft.id },
        {
          onSuccess: () => {
            enqueueSnackbar('Draft deleted successfully', { variant: 'success' });
            refetchData();
            closeDeleteDialog();
          },
          onError: () => {
            enqueueSnackbar('Failed to delete draft', { variant: 'error' });
          },
        }
      );
    } else {
      enqueueSnackbar('No draft selected for deletion', { variant: 'warning' });
    }
  };

  const handleRename = (name: string) => {
    if (selectedDraft) {
      mutateSaveDraftContent(
        {
          videoName: name,
          avatarId: selectedDraft.avatarId,
          audioId: selectedDraft.userAudioTrackId,
          text: selectedDraft.speechContent?.content || '',
          creationId: selectedDraft.id,
        },
        {
          onSuccess: () => {
            enqueueSnackbar('Draft renamed successfully', { variant: 'success' });
            refetchData();
            closeRenameDialog();
          },
          onError: () => {
            enqueueSnackbar('Failed to rename draft', { variant: 'error' });
          },
        }
      );
    } else {
      enqueueSnackbar('No draft selected for renaming', { variant: 'warning' });
    }
  };

  const handleOnSuccessEdit = () => {
    refetchData();
  };

  return {
    isDeleteDialogOpen,
    handleOpenDeleteDialog,
    closeDeleteDialog,
    handleConfirmDelete,
    isRenameDialogOpen,
    handleOpenRenameDialog,
    closeRenameDialog,
    handleRename,
    isDeleteLoading,
    isRenameLoading,
    selectedDraft,
    isViewDialogOpen,
    handleOpenViewDialog,
    closeViewDialog,
    handleOnSuccessEdit,
  };
};
